import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Package, Mail, ArrowLeft, Truck, Gift, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useCart } from '../contexts/CartContext';
import Navbar from './Navbar';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);

  const PROMO_CODE = 'GRACIAS10';
  const copyPromo = () => {
    navigator.clipboard?.writeText(PROMO_CODE);
    toast.success('¡Código copiado!');
  };

  // Clear cart on mount (one-shot)
  useEffect(() => {
    clearCart();
  }, [clearCart]);

  // Poll for order status (webhook may take a moment)
  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/tools/checkout/status/${sessionId}`);
        if (cancelled) return;
        setOrder(res.data);
        setLoading(false);
        if (res.data?.status !== 'paid' && attempts < 8) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        }
      } catch (err) {
        if (!cancelled) {
          if (attempts < 8) {
            setTimeout(() => setAttempts((a) => a + 1), 2000);
          } else {
            setLoading(false);
          }
        }
      }
    };
    fetchStatus();
    return () => {
      cancelled = true;
    };
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50">
      <Navbar />
      <div className="pt-24 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <Card className="border-0 shadow-2xl overflow-hidden" data-testid="order-success-card">
          <div className="bg-gradient-to-r from-green-600 to-emerald-500 text-white p-8 text-center">
            <CheckCircle size={64} className="mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl font-bold">¡Pago Exitoso!</h1>
            <p className="text-green-50 mt-2">Tu pedido ha sido recibido y confirmado.</p>
          </div>
          <CardContent className="p-8">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent mx-auto" />
                <p className="text-slate-500 mt-3">Confirmando tu pedido...</p>
              </div>
            ) : order ? (
              <>
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <p className="text-xs uppercase text-slate-500 font-semibold mb-1">
                    N° de Pedido
                  </p>
                  <p className="font-mono text-sm text-slate-900" data-testid="order-id">
                    {order.id}
                  </p>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-slate-500">Total pagado</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {order.currency_symbol}
                        {Number(order.subtotal || 0).toFixed(2)}{' '}
                        <span className="text-xs">{(order.currency || '').toUpperCase()}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Estado</p>
                      <p className="text-base font-semibold text-green-700 capitalize">
                        {order.status === 'paid' ? '✅ Pagado' : '⏳ Procesando...'}
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Package size={18} className="text-red-600" /> Productos
                </h3>
                <div className="space-y-2 mb-6">
                  {(order.items || []).map((it, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between text-sm py-2 border-b border-slate-100 last:border-0"
                    >
                      <span className="text-slate-700">
                        {it.name} <span className="text-slate-400">× {it.quantity}</span>
                      </span>
                      <span className="font-semibold text-slate-900 tabular-nums">
                        {order.currency_symbol}
                        {Number(it.subtotal).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <Truck size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900 text-sm">
                        Próximos pasos: cotización de envío
                      </p>
                      <p className="text-xs text-amber-800 mt-1">
                        En las próximas 24-48 horas te enviaremos un email con el coste del envío a
                        tu dirección y la información de seguimiento.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex gap-3">
                  <Mail size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    Hemos enviado un email de confirmación a{' '}
                    <strong>{order.customer_email}</strong>.
                  </div>
                </div>
              </>
            ) : (
              <p className="text-center text-slate-500 py-8">
                No pudimos cargar los detalles del pedido. Revisa tu email — recibirás la
                confirmación en breve.
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Link to="/tienda" className="flex-1">
                <Button
                  data-testid="order-back-store"
                  variant="outline"
                  className="w-full border-slate-300"
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Seguir comprando
                </Button>
              </Link>
              <Link to="/" className="flex-1">
                <Button
                  data-testid="order-back-home"
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                >
                  Volver al inicio
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Post-purchase upsell */}
        <Card
          className="border-0 shadow-xl overflow-hidden mt-6 bg-gradient-to-br from-red-600 to-red-700 text-white"
          data-testid="upsell-card"
        >
          <CardContent className="p-6 sm:p-8 text-center">
            <Gift size={40} className="mx-auto mb-3 text-red-100" />
            <h3 className="text-2xl font-bold">¡Gracias por tu compra! 🎉</h3>
            <p className="text-red-50 mt-2 text-sm sm:text-base">
              Usa este código y llévate un <strong>10% de descuento</strong> en tu próxima
              herramienta.
            </p>
            <div className="mt-5 inline-flex items-center gap-3 bg-white/15 backdrop-blur rounded-xl px-5 py-3 border border-white/30">
              <span
                data-testid="upsell-code"
                className="text-2xl font-mono font-extrabold tracking-widest"
              >
                {PROMO_CODE}
              </span>
              <button
                data-testid="upsell-copy"
                onClick={copyPromo}
                className="bg-white text-red-700 rounded-lg p-2 hover:bg-red-50 transition-colors"
                aria-label="Copiar código"
              >
                <Copy size={18} />
              </button>
            </div>
            <div className="mt-5">
              <Link to="/tienda">
                <Button
                  data-testid="upsell-shop-btn"
                  className="bg-white text-red-700 hover:bg-red-50 font-semibold px-8"
                >
                  Usar mi descuento ahora
                </Button>
              </Link>
            </div>
            <p className="text-[11px] text-red-100/80 mt-3">
              Aplica el código en el carrito antes de pagar.
            </p>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default OrderSuccess;
